import React, { useEffect, useState } from 'react';
import { ANNOTATION_COLORS, BRUSH_SIZES } from './annotationProtocol';

/**
 * Small movable toolbar: Pen, Eraser, Undo, Clear, Close + colors/sizes.
 */
export default function FloatingAnnotationToolbar({ controller }) {
  const [, setTick] = useState(0);
  const [offset, setOffset] = useState({ x: 12, y: 56 });
  const dragging = React.useRef(null);

  useEffect(() => controller.subscribe(() => setTick((t) => t + 1)), [controller]);

  const onDragHandleDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragging.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: offset.x,
      origY: offset.y,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onDragHandleMove = (e) => {
    if (!dragging.current || dragging.current.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    const dx = e.clientX - dragging.current.startX;
    const dy = e.clientY - dragging.current.startY;
    setOffset({
      x: Math.max(0, dragging.current.origX + dx),
      y: Math.max(0, dragging.current.origY + dy),
    });
    controller.bumpToolbarActivity();
  };

  const onDragHandleUp = (e) => {
    if (!dragging.current || dragging.current.pointerId !== e.pointerId) return;
    dragging.current = null;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }
  };

  const expanded = controller.toolbarExpanded && controller.enabled;
  const showChip = !expanded;

  return (
    <div
      className="absolute z-[50] select-none"
      style={{ left: offset.x, top: offset.y, pointerEvents: 'auto' }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {showChip ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!controller.enabled) controller.setEnabled(true);
            controller.expandToolbar();
          }}
          className="flex items-center gap-1 rounded-full bg-black/80 px-3 py-2 text-xs text-white shadow cursor-pointer"
          title="Draw on the other person's shared screen"
        >
          Annotate
        </button>
      ) : (
        <div className="rounded-xl bg-black/80 px-2 py-1.5 text-white shadow-lg">
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              title="Drag"
              aria-label="Drag toolbar"
              className="cursor-grab active:cursor-grabbing rounded-md px-2 py-1 text-sm hover:bg-white/10 touch-none"
              onPointerDown={onDragHandleDown}
              onPointerMove={onDragHandleMove}
              onPointerUp={onDragHandleUp}
              onPointerCancel={onDragHandleUp}
            >
              ::
            </button>
            <ToolBtn
              label="Pen"
              active={controller.tool === 'pen'}
              onClick={() => controller.setTool('pen')}
            >
              Pen
            </ToolBtn>
            <ToolBtn
              label="Eraser"
              active={controller.tool === 'eraser'}
              onClick={() => controller.setTool('eraser')}
            >
              Erase
            </ToolBtn>
            <ToolBtn label="Undo" onClick={() => controller.undo()}>Undo</ToolBtn>
            <ToolBtn label="Clear" onClick={() => controller.clearAll()}>Clear</ToolBtn>
            <ToolBtn label="Close" onClick={() => controller.setEnabled(false)}>Close</ToolBtn>
          </div>
          {controller.tool === 'pen' && (
            <div className="mt-1.5 flex items-center gap-1">
              {Object.keys(ANNOTATION_COLORS).map((name) => (
                <button
                  key={name}
                  type="button"
                  aria-label={name}
                  onClick={(e) => {
                    e.stopPropagation();
                    controller.setColor(name);
                  }}
                  className="h-[18px] w-[18px] rounded-full border cursor-pointer"
                  style={{
                    background: ANNOTATION_COLORS[name],
                    borderColor: controller.color === name ? '#fff' : 'rgba(255,255,255,0.25)',
                    borderWidth: controller.color === name ? 2 : 1,
                  }}
                />
              ))}
              <span className="mx-1 w-px self-stretch bg-white/20" />
              {Object.keys(BRUSH_SIZES).map((name) => (
                <button
                  key={name}
                  type="button"
                  aria-label={name}
                  onClick={(e) => {
                    e.stopPropagation();
                    controller.setBrushSize(name);
                  }}
                  className={`flex h-[22px] w-[22px] items-center justify-center rounded-md cursor-pointer ${
                    controller.brushSize === name ? 'bg-white/20' : ''
                  }`}
                >
                  <span
                    className="rounded-full bg-white"
                    style={{
                      width: BRUSH_SIZES[name] + 2,
                      height: BRUSH_SIZES[name] + 2,
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ToolBtn({ children, onClick, active, label }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={`rounded-md px-2 py-1 text-sm cursor-pointer ${
        active ? 'bg-sky-500/40' : 'hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  );
}
